import { useEffect, useState } from "react";
import { Clock, CheckCircle2, XCircle, Ban, Sparkles, Users, DollarSign, BarChart3 } from "lucide-react";
import { supabase } from "../lib/supabase";
import type { AdsDecision } from "../lib/types";
import { ACTION_LABEL, TIER_LABEL, fmtDate } from "../lib/format";
import PageHeader from "../components/ui/PageHeader";
import { ChannelLogo } from "../components/ui/ChannelLogo";

const CATEGORY_LABEL: Record<string, { label: string; color: string }> = {
  tofu_awareness: { label: "TOFU · Awareness",    color: "bg-[#e0e7ff] text-[#3730a3]" },
  tofu_traffic:   { label: "TOFU · Tráfego",      color: "bg-[#e0e7ff] text-[#3730a3]" },
  mofu_engagement:{ label: "MOFU · Engajamento",  color: "bg-[#fef3c7] text-[#92400e]" },
  mofu_leads:     { label: "MOFU · Leads",        color: "bg-[#fef3c7] text-[#92400e]" },
  bofu_sales:     { label: "BOFU · Vendas",       color: "bg-[#dcfce7] text-[#166534]" },
  remarketing:    { label: "Remarketing",         color: "bg-[#fce7f3] text-[#9d174d]" },
  branded_search: { label: "Search · Marca",      color: "bg-[#dbeafe] text-[#1e40af]" },
  generic_search: { label: "Search · Genérico",   color: "bg-[#e5e7eb] text-[#374151]" },
  shopping_pmax:  { label: "Shopping / PMax",     color: "bg-[#fae8ff] text-[#86198f]" },
  unknown:        { label: "Categoria?",          color: "bg-[#fee2e2] text-[#991b1b]" },
};

export default function Pending() {
  const [decisions, setDecisions] = useState<AdsDecision[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = async () => {
    const { data } = await supabase.from("ads_decisions").select("*").eq("status", "pending").order("created_at", { ascending: false });
    setDecisions((data as AdsDecision[]) ?? []);
  };

  useEffect(() => {
    reload().then(() => setLoading(false));
    const ch = supabase.channel("pending-changes").on("postgres_changes", { event: "*", schema: "public", table: "ads_decisions" }, () => reload()).subscribe();
    return () => { ch.unsubscribe(); };
  }, []);

  const decide = async (id: string, status: "approved" | "rejected") => {
    setBusy(id);
    // Otimismo: remove da lista local imediatamente, antes do round-trip
    setDecisions((prev) => prev.filter((d) => d.id !== id));
    const { error } = await supabase.from("ads_decisions").update({
      status,
      approved_by: "dashboard",
      approved_at: new Date().toISOString(),
    }).eq("id", id);
    setBusy(null);
    if (error) {
      console.error("decide falhou:", error);
      alert(`Erro ao salvar: ${error.message}`);
      // Reverte: re-puxa lista
      await reload();
    }
  };

  return (
    <>
      <PageHeader
        title="Decisões pendentes"
        subtitle={`${decisions.length} aguardando revisão`}
        icon={<Clock size={22} strokeWidth={2} />}
      />

      {loading ? (
        <p className="text-esm-gray-400 italic">Carregando…</p>
      ) : decisions.length === 0 ? (
        <div className="bg-white border border-esm-gray-100 rounded-2xl p-12 text-center shadow-card">
          <p className="text-4xl mb-3">🎉</p>
          <p className="font-bold text-esm-gray-900 text-lg">Nada pendente</p>
          <p className="text-sm text-esm-gray-400 mt-1">O agente roda diariamente às 08h. Use /rodar no Telegram pra disparar on-demand.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {decisions.map((d) => <Card key={d.id} d={d} onDecide={decide} busy={busy === d.id} />)}
        </div>
      )}
    </>
  );
}

function tierColor(t: string) {
  return t === "T1" ? "bg-[#dcfce7] text-[#166534]" : t === "T2" ? "bg-[#fef3c7] text-[#92400e]" : "bg-[#fee2e2] text-[#991b1b]";
}
function confColor(c: string) {
  return c === "high" ? "bg-[#dcfce7] text-[#166534]" : c === "medium" ? "bg-[#fef3c7] text-[#92400e]" : "bg-[#ffedd5] text-[#9a3412]";
}

function Card({ d, onDecide, busy }: { d: AdsDecision; onDecide: (id: string, status: "approved" | "rejected") => Promise<void>; busy: boolean }) {
  const cat = d.inferred_category ? CATEGORY_LABEL[d.inferred_category] : null;
  const spec = d.action_specifics ?? {};
  return (
    <div className="bg-white border border-esm-gray-100 rounded-2xl p-5 shadow-card hover:shadow-card-hover transition-shadow">
      {/* Header: logo do canal + tags */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-md bg-esm-cream-50 text-esm-brown-700 border border-esm-brown-100">
            <ChannelLogo channel={d.channel} size={12} />
            <span className="capitalize">{d.channel}</span>
          </span>
          {cat && (
            <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${cat.color}`}>{cat.label}</span>
          )}
          <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${tierColor(d.tier)}`}>{d.tier} · {TIER_LABEL[d.tier]}</span>
          <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${confColor(d.confidence)}`}>conf {d.confidence}</span>
        </div>
      </div>

      {/* Ação principal */}
      <p className="text-lg font-bold text-esm-gray-900 leading-tight">
        {ACTION_LABEL[d.action] ?? d.action}
        {d.delta_value ? <span className="text-esm-brown-700"> ({d.delta_value > 0 ? "+" : ""}{d.delta_value}{d.delta_unit === "percent" ? "%" : d.delta_unit === "BRL" ? " R$" : ""})</span> : null}
        {spec.budget_target_brl ? <span className="text-esm-brown-700 text-sm font-semibold ml-1">→ R$ {spec.budget_target_brl}/dia</span> : null}
      </p>
      <p className="text-xs text-esm-gray-400 mt-1">{d.target_type}: <code className="bg-esm-cream-50 px-1 rounded text-esm-brown-700">{d.target_id}</code></p>
      <p className="text-sm font-semibold text-esm-gray-700 mt-1">{d.target_name}</p>

      {/* Métrica avaliada (banner) */}
      {(spec.primary_metric_label || spec.evaluated_metric_value) && (
        <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-esm-cream-50 border border-esm-brown-100">
          <BarChart3 size={14} className="text-esm-brown-700 flex-shrink-0" />
          <p className="text-xs text-esm-brown-900">
            <span className="font-bold">{spec.primary_metric_label ?? d.evaluated_metric ?? "Métrica"}: </span>
            <span className="tabular-nums">{spec.evaluated_metric_value ?? "—"}</span>
          </p>
        </div>
      )}

      <div className="my-4 border-t border-esm-gray-100" />
      <p className="text-sm text-esm-gray-700"><span className="font-bold text-esm-gray-900">Por quê: </span>{d.rationale}</p>

      {/* Specifics: keywords a negativar */}
      {spec.negative_keywords && spec.negative_keywords.length > 0 && (
        <div className="mt-3 bg-red-50/60 border border-red-200/60 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Ban size={13} className="text-red-700" />
            <p className="text-[11px] font-bold uppercase tracking-wider text-red-900">Negativar termos</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {spec.negative_keywords.map((kw) => (
              <code key={kw} className="text-xs font-mono bg-white px-2 py-0.5 rounded border border-red-200 text-red-900">{kw}</code>
            ))}
          </div>
          {spec.search_term_evidence && (
            <p className="text-[11px] text-red-900/80 mt-2 italic">Evidência: {spec.search_term_evidence}</p>
          )}
        </div>
      )}

      {/* Specifics: refresh criativo */}
      {(spec.creative_diagnosis || spec.refresh_direction) && (
        <div className="mt-3 bg-amber-50/60 border border-amber-200/60 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles size={13} className="text-amber-700" />
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-900">Refresh criativo</p>
          </div>
          {spec.creative_diagnosis && (
            <p className="text-xs text-amber-900"><span className="font-semibold">Diagnóstico:</span> {spec.creative_diagnosis}</p>
          )}
          {spec.worst_creative_id && (
            <p className="text-xs text-amber-900 mt-1"><span className="font-semibold">Pior ad:</span> <code className="bg-white px-1 rounded">{spec.worst_creative_id}</code></p>
          )}
          {spec.refresh_direction && (
            <p className="text-xs text-amber-900 mt-1.5"><span className="font-semibold">Direção sugerida:</span> {spec.refresh_direction}</p>
          )}
        </div>
      )}

      {/* Specifics: audience */}
      {spec.audience_direction && (
        <div className="mt-3 bg-violet-50/60 border border-violet-200/60 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Users size={13} className="text-violet-700" />
            <p className="text-[11px] font-bold uppercase tracking-wider text-violet-900">Audience</p>
          </div>
          <p className="text-xs text-violet-900">{spec.audience_direction}</p>
        </div>
      )}

      {/* Specifics: budget alvo */}
      {spec.budget_target_brl !== undefined && spec.budget_target_brl > 0 && (
        <div className="mt-3 bg-emerald-50/60 border border-emerald-200/60 rounded-xl p-3">
          <div className="flex items-center gap-1.5">
            <DollarSign size={13} className="text-emerald-700" />
            <p className="text-xs text-emerald-900"><span className="font-bold">Novo budget diário:</span> R$ {spec.budget_target_brl}</p>
          </div>
        </div>
      )}

      {d.expected_impact && <p className="text-sm text-esm-gray-700 mt-3"><span className="font-bold text-esm-gray-900">Impacto esperado: </span>{d.expected_impact}</p>}
      <p className="text-[11px] text-esm-gray-400 mt-3">Análise {d.run_date} · gerada em {fmtDate(d.created_at)}</p>

      <div className="flex gap-2 mt-4">
        <button
          type="button"
          onClick={() => onDecide(d.id, "approved")}
          disabled={busy}
          className="flex-1 flex items-center justify-center gap-2 bg-esm-success/10 text-esm-success border border-esm-success/30 hover:bg-esm-success hover:text-white text-sm font-bold py-2 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-wait"
        >
          <CheckCircle2 size={16} /> {busy ? "Salvando…" : "Aprovar"}
        </button>
        <button
          type="button"
          onClick={() => onDecide(d.id, "rejected")}
          disabled={busy}
          className="flex-1 flex items-center justify-center gap-2 bg-esm-error/10 text-esm-error border border-esm-error/30 hover:bg-esm-error hover:text-white text-sm font-bold py-2 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-wait"
        >
          <XCircle size={16} /> {busy ? "Salvando…" : "Rejeitar"}
        </button>
      </div>
    </div>
  );
}
